import { Key } from "ts-key-enum";
import {
  InjectionKey,
  Ref,
  WatchStopHandle,
  computed,
  inject,
  provide,
  reactive,
  ref,
  shallowReadonly,
  watch,
  onUnmounted,
} from "vue";
import { isFunction, promisedTimeout, useEvent } from "vue-composable";

let loadingCount = 0;
export function startLoading() {
  return ++loadingCount;
}

export function stopLoading() {
  return --loadingCount;
}

async function waitLoading() {
  while (loadingCount > 0) {
    await new Promise((resolve) => setTimeout(resolve, 10));
  }
}

function getCSSPath(el: Element | null): string | undefined {
  if (!el || !(el instanceof Element)) return undefined;

  const path: string[] = [];
  while (el && el.nodeType === Node.ELEMENT_NODE) {
    let selector = el.nodeName.toLowerCase();
    if (el.id) {
      selector += `#${el.id}`;
      path.unshift(selector);
      break;
    } else {
      let sibling: Element | null = el;
      let nth = 1;
      while ((sibling = sibling.previousElementSibling)) {
        if (sibling.nodeName.toLowerCase() === selector) nth++;
      }
      if (nth !== 1) selector += `:nth-of-type(${nth})`;
    }
    path.unshift(selector);
    el = el.parentNode as Element | null;
  }

  return path.join(" > ");
}

const FocusHistorySymbol = Symbol(
  IS_APP && import.meta.env.DEV ? "FocusHistory" : undefined,
) as InjectionKey<{
  queue: readonly HTMLElement[];
  focusLast: () => void;
}>;

export function defineFocusHistory(queueMax: number | undefined = 500) {
  const queue = reactive<HTMLElement[]>([]);
  const cssMap = new WeakMap<HTMLElement, string | undefined>();
  useEvent(
    window,
    "focus",
    (e) => {
      const target = e.relatedTarget ?? e.target;

      if (
        target instanceof HTMLElement && // ensure it's a real element
        document.body.contains(target) && // safe now
        isAllowedToFocus(target)
      ) {
        queue.push(target as HTMLElement);
        cssMap.set(target as HTMLElement, getCSSPath(target as HTMLElement));
      }
    },
    {
      capture: true,
      passive: true,
    },
  );

  // ENG-21464: macOS buttons don't get real DOM focus on click, so also track mousedown.
  useEvent(
    window,
    "mousedown",
    (e) => {
      const target = e.target;

      if (
        target instanceof HTMLElement &&
        document.body.contains(target) &&
        isAllowedToFocus(target) &&
        target.matches(focusableSelector)
      ) {
        queue.push(target);
        cssMap.set(target, getCSSPath(target));
      }
    },
    {
      capture: true,
      passive: true,
    },
  );

  async function focusLast() {
    let item;

    await waitLoading();
    loadingCount = 0;

    await promisedTimeout(10);
    while ((item = queue.pop())) {
      if (!document.body.contains(item)) {
        const p = cssMap.get(item);
        if (p) {
          try {
            const e = document.querySelector(p);
            if (e && e instanceof HTMLElement && e.focus) {
              e.focus();
              return;
            }
          } catch {
            //ignore
          }
        }
        continue;
      }
      if (item && item instanceof HTMLElement && item.focus) {
        item.focus();
        return;
      }
    }
  }

  if (queueMax) {
    watch(
      () => queue.length,
      (e) => {
        if (queueMax >= e) return;
        // while (e > queueMax) {
        //   queue.shift();
        // }
        while (queue.length > queueMax) {
          queue.shift();
        }
      },
      {
        flush: "post",
      },
    );
  }

  const result = {
    queue: shallowReadonly(queue),

    focusLast,
  };

  provide(FocusHistorySymbol, result);

  return result;
}

export function useFocusHistory() {
  return inject(FocusHistorySymbol);
}

const focusableSelector = [
  'button:not([disabled]):not([disable]):not([tabindex="-1"])',
  'a[href]:not([tabindex="-1"])',
  'input:not([disabled]):not([disable]):not([tabindex="-1"])',
  'select:not([disabled]):not([disable]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([disable]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"]):not([disabled]):not([disable])',
].join(", ");

function isAllowedToFocus(x: HTMLElement) {
  if (!isFunction(x.getAttribute)) return undefined;
  return (
    x.getAttribute("tabindex") !== "-1" && !x.hasAttribute("disabled") && !x.hasAttribute("disable")
  );
}

// ENG-21464: drills [data-focus-first] containers (e.g. a fieldset) down to their first real control.
const nativeControlSelector = [
  'button:not([disabled]):not([disable]):not([tabindex="-1"])',
  'a[href]:not([tabindex="-1"])',
  'input:not([disabled]):not([disable]):not([tabindex="-1"])',
  'select:not([disabled]):not([disable]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([disable]):not([tabindex="-1"])',
].join(", ");

function resolveFocusTarget(el: HTMLElement): HTMLElement {
  if (el.matches(nativeControlSelector)) return el;
  return el.querySelector<HTMLElement>(nativeControlSelector) ?? el;
}

// ENG-21464: fallback for forms with no [data-focus-first] marker - real form controls only.
const formFieldSelector = [
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([disabled]):not([disable]):not([readonly]):not([tabindex="-1"])',
  'select:not([disabled]):not([disable]):not([readonly]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([disable]):not([readonly]):not([tabindex="-1"])',
].join(", ");

// ENG-21464: marks the auto-focused element so components can distinguish it from a user focus.
export const AUTO_FOCUS_ATTR = "data-auto-focus-active";

export function useFocusTrap(
  el: Ref<HTMLElement | undefined | null>,
  autoFocus: boolean | number,
  returnFocus: boolean = false,
  // ENG-21464: pass when captured synchronously at open time (see DrawerItem.openedFrom).
  openedFrom?: HTMLElement | null,
) {
  const focusQueue = useFocusHistory();

  const focusableContent = ref<HTMLElement[] | null | undefined>(null);
  const runCount = ref(0);
  const startFocusOn = ref<HTMLElement | null>(null);

  // ENG-21464: fall back to the queue's most recent entry, not queue[0] (the oldest retained).
  const startedWithFocusOn: HTMLElement | null =
    openedFrom ??
    (focusQueue?.queue.length
      ? focusQueue.queue[focusQueue.queue.length - 1]
      : (document.activeElement as HTMLElement | null));

  const observer = new MutationObserver(refreshElements);

  function isDomNode(x: unknown): x is Node {
    return typeof Node !== "undefined" && x instanceof Node;
  }

  let focusTarget: EventTarget | null = null;

  // ENG-21464: any keystroke/input inside the trap expires the upgrade window below.
  let interactionTarget: HTMLElement | null = null;
  function expireUpgradeWindow() {
    upgradeExpired = true;
  }

  watch(el, (e) => {
    setupObserver(e);
  });

  function setupObserver(e: HTMLElement | null | undefined) {
    try {
      observer?.disconnect();
    } catch {
      // ignore
    }

    if (interactionTarget) {
      interactionTarget.removeEventListener("keydown", expireUpgradeWindow, true);
      interactionTarget.removeEventListener("input", expireUpgradeWindow, true);
      interactionTarget = null;
    }

    const target: Node | null = isDomNode(e) ? e : isDomNode(el?.value) ? el!.value : null;

    if (target) {
      Promise.resolve().then(refreshElements);
      try {
        observer.observe(target, {
          childList: true,
          subtree: true,
          attributeFilter: ["tabindex", "disable", "disabled"],
          attributes: true,
        });
      } catch (err) {
        // As a final safeguard, avoid throwing in production
        console.warn("MutationObserver.observe failed:", err);
      }
      interactionTarget = target as HTMLElement;
      interactionTarget.addEventListener("keydown", expireUpgradeWindow, true);
      interactionTarget.addEventListener("input", expireUpgradeWindow, true);
    } else {
      focusTarget = null;
    }
  }

  function refreshElements() {
    // Collect allowed focusables directly via the selector (no extra filter needed)
    focusableContent.value = Array.from(
      el?.value?.querySelectorAll<HTMLElement>(focusableSelector) ?? [],
    );

    handleStartFocus();
  }

  // ENG-21464: allows upgrading to an earlier-mounting field until the user interacts; no fixed timer.
  let upgradeExpired = false;

  // ENG-21464: marks the element so components can show a focus ring :focus-visible would suppress.
  function markAutoFocused(el: HTMLElement) {
    el.setAttribute(AUTO_FOCUS_ATTR, "");
    el.addEventListener("blur", () => el.removeAttribute(AUTO_FOCUS_ATTR), { once: true });
    el.focus();
  }

  function handleStartFocus() {
    if (!focusableContent.value || focusableContent.value.length === 0) return;

    // Explicit [data-focus-first] wins; otherwise the first real form field.
    const target =
      focusableContent.value.find((el: HTMLElement) => el.hasAttribute("data-focus-first")) ??
      focusableContent.value.find((el: HTMLElement) => el.matches(formFieldSelector));

    if (runCount.value === 0) {
      // Only latch once a target is found, so a later MutationObserver refresh gets another try.
      if (target) {
        const focusTarget = resolveFocusTarget(target);
        startFocusOn.value = focusTarget;
        markAutoFocused(focusTarget);
        runCount.value++;
        upgradeExpired = false;
      }
      return;
    }

    // Within the upgrade window, switch to an earlier-mounting field if the user hasn't moved focus.
    if (
      !upgradeExpired &&
      target &&
      target !== startFocusOn.value &&
      document.activeElement === startFocusOn.value
    ) {
      const focusTarget = resolveFocusTarget(target);
      startFocusOn.value = focusTarget;
      markAutoFocused(focusTarget);
    }
  }

  // can be simplified when upgraded to Vue > 3.5
  // could be built out to be more selective, ie first instance of x.
  const index =
    autoFocus === true ? 0 : typeof autoFocus === "number" ? Math.max(0, autoFocus) : null;

  if (index !== null) {
    const stop: WatchStopHandle | undefined = watch(
      focusableContent,
      (els) => {
        const el = startFocusOn.value ? startFocusOn.value : els?.[index];
        if (el?.focus) {
          el.focus();
          // defer so `stop` is initialized before we call it
          queueMicrotask(() => stop?.());
        }
      },
      { flush: "post", immediate: true },
    );
  }

  const resetOnCurrentRef = (modalRef: unknown) => {
    runCount.value = 0;
    upgradeExpired = false;
    setupObserver(isDomNode(modalRef) ? (modalRef as HTMLElement) : null);
  };

  const first = computed(() => focusableContent.value?.[0]);
  const last = computed(() =>
    focusableContent.value ? focusableContent.value[focusableContent.value.length - 1] : undefined,
  );

  useEvent(
    // ENG-21695 el is a Vue Ref; useEvent's overloads type against vue-composable's Ref (different RefSymbol brand)
    el as any,
    "focus",
    (e: FocusEvent) => {
      focusTarget = e.target;
    },
    {
      capture: true,
    },
  );

  useEvent(document, "keydown", (e) => {
    if (!el.value) return;
    const isTabPressed = e.key === Key.Tab || e.keyCode === 9;

    if (!isTabPressed) {
      return;
    }

    if (e.shiftKey || e.metaKey) {
      if (focusTarget === first.value) {
        last.value?.focus();
        e.preventDefault();
      }
    } else {
      if (focusTarget === last.value) {
        first.value?.focus();
        e.preventDefault();
      }
    }
  });

  const returnFocusToParent = () => {
    if (!returnFocus) return;
    // ENG-21464: deferred so it runs after MedicusLayout's inert-lock cleanup microtask (ENG-16120).
    requestAnimationFrame(() => {
      if (startedWithFocusOn && document.body.contains(startedWithFocusOn)) {
        startedWithFocusOn.focus();
        return;
      }
      // ENG-21464: captured opener is gone - fall back to the shared focus-history queue.
      focusQueue?.focusLast();
    });
  };

  onUnmounted(() => {
    if (interactionTarget) {
      interactionTarget.removeEventListener("keydown", expireUpgradeWindow, true);
      interactionTarget.removeEventListener("input", expireUpgradeWindow, true);
    }
    returnFocusToParent();
  });

  return {
    resetOnCurrentRef,
    focusableContent,
    first,
    last,
  };
}
